import { useEffect, useRef, useState } from 'react';
import { RefreshCw, MapPin } from 'lucide-react';
import { loadGoogleMaps, isGoogleMapsLoaded, getGoogleMapsApi } from '../lib/googleMaps';
import type { GPSLocation } from '../lib/googleMaps';
import { MapErrorBoundary } from './MapErrorBoundary';

interface LocationPickerProps {
  /** Starting pin position (usually the device's current GPS reading). */
  initial: GPSLocation;
  /** GPS accuracy in metres; drawn as a translucent circle around the seed so
   *  the tech can see how rough the auto-fix was. */
  accuracyMeters?: number | null;
  height?: string;
  /** Fires (on each pan settle) with the coordinate under the centre pin. */
  onChange?: (loc: GPSLocation) => void;
  /** Localised hint shown over the map. Defaults to English. */
  hint?: string;
}

/**
 * Map location picker using the "fixed centre pin" pattern: the pin stays
 * pinned to the centre of the map and the tech moves the *map* underneath it
 * (one-finger pan), then saves. This is far more reliable on touch than a
 * draggable marker — which on iOS/Safari needs a long-press to grab and a
 * normal touch just pans. The confirmed centre coordinate is what gets saved,
 * so a poor GPS fix can never silently save the wrong spot. Built standalone so
 * it can back the real "Set GPS" flow.
 */
export function LocationPicker(props: LocationPickerProps) {
  return (
    <MapErrorBoundary height={props.height ?? '320px'}>
      <LocationPickerInner {...props} />
    </MapErrorBoundary>
  );
}

function LocationPickerInner({ initial, accuracyMeters, height = '320px', onChange, hint }: LocationPickerProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [mapReady, setMapReady] = useState(isGoogleMapsLoaded());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGoogleMaps()
      .then(() => {
        // Resolving is not proof the API is usable — check the global itself.
        if (isGoogleMapsLoaded()) setMapReady(true);
        else setError('Failed to load Google Maps.');
      })
      .catch((e) => {
        console.error('Failed to load Google Maps for the location picker:', e);
        setError('Failed to load Google Maps.');
      });
  }, []);

  // Create the map once the API is ready. The centre of the map is the
  // selection; report it whenever a pan/zoom settles.
  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstanceRef.current) return;
    // Never touch the bare `google` global: it throws a ReferenceError rather
    // than reading as undefined when the third-party script isn't there.
    const maps = getGoogleMapsApi();
    if (!maps) {
      setError('Failed to load Google Maps.');
      return;
    }
    const center = { lat: initial.latitude, lng: initial.longitude };

    const map = new maps.Map(mapRef.current, {
      center,
      zoom: 19,
      mapTypeId: maps.MapTypeId.HYBRID,
      gestureHandling: 'greedy', // one-finger pan on touch (no two-finger requirement)
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      clickableIcons: false,
    });
    map.addListener('idle', () => {
      const c = map.getCenter();
      if (c) onChangeRef.current?.({ latitude: c.lat(), longitude: c.lng() });
    });
    mapInstanceRef.current = map;
  }, [mapReady, initial]);

  // Draw / refresh the GPS-accuracy circle at the seed.
  useEffect(() => {
    const maps = getGoogleMapsApi();
    if (!mapReady || !mapInstanceRef.current || !maps) return;
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
    if (accuracyMeters && accuracyMeters > 0) {
      circleRef.current = new maps.Circle({
        map: mapInstanceRef.current,
        center: { lat: initial.latitude, lng: initial.longitude },
        radius: accuracyMeters,
        fillColor: '#3b82f6',
        fillOpacity: 0.12,
        strokeColor: '#3b82f6',
        strokeOpacity: 0.4,
        strokeWeight: 1,
        clickable: false,
      });
    }
  }, [mapReady, initial, accuracyMeters]);

  // Recenter the map when a fresh GPS seed is supplied.
  useEffect(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setCenter({ lat: initial.latitude, lng: initial.longitude });
    }
  }, [initial]);

  if (error) {
    return (
      <div className="w-full rounded-lg border border-red-200 bg-red-50 flex items-center justify-center text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400 text-sm" style={{ height }}>
        {error}
      </div>
    );
  }
  if (!mapReady) {
    return (
      <div className="w-full rounded-lg border border-line bg-subtle flex items-center justify-center text-theme-secondary text-sm gap-2" style={{ height }}>
        <RefreshCw size={16} className="animate-spin" /> Loading map…
      </div>
    );
  }
  return (
    <div className="relative w-full rounded-lg overflow-hidden border border-line shadow-sm" style={{ height }}>
      <div ref={mapRef} className="w-full h-full" />
      {/* Fixed centre pin: the visual marker sits above the exact target dot. */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <MapPin size={40} fill="#dc2626" className="text-red-700 drop-shadow-md" style={{ transform: 'translateY(-50%)' }} />
      </div>
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-2 w-2 -ml-1 -mt-1 rounded-full bg-red-700 ring-2 ring-white" />
      <div className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white whitespace-nowrap">
        {hint ?? 'Drag the map to move the pin'}
      </div>
    </div>
  );
}
